package pl.nickers.match3

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.filled.StarBorder
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import pl.nickers.match3.ui.GemChip

private enum class LevelStatus { Locked, Available, Completed }

@Composable
fun JourneyScreen(
    service: JourneyService,
    livesInfo: LivesInfo,
    /** Gem balance for the header chip (E18/F18.4). Supplied by the caller; never computed here. */
    gemBalance: Int,
    onLevelSelected: (Int) -> Unit,
    onBack: () -> Unit,
) {
    var highestUnlocked by remember { mutableStateOf(service.highestUnlockedLevel) }
    var showResetConfirm by remember { mutableStateOf(false) }

    if (showResetConfirm) {
        AlertDialog(
            onDismissRequest = { showResetConfirm = false },
            title = { Text("Reset progress?") },
            text = { Text("All level progress and star ratings will be lost and you'll start from level 1 again.") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showResetConfirm = false
                        service.resetProgress()
                        highestUnlocked = service.highestUnlockedLevel
                    },
                    colors = ButtonDefaults.textButtonColors(
                        contentColor = MaterialTheme.colorScheme.error,
                    ),
                ) { Text("Reset") }
            },
            dismissButton = {
                TextButton(onClick = { showResetConfirm = false }) { Text("Cancel") }
            },
        )
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.92f)),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TextButton(onClick = onBack) { Text("Back") }
            Text(
                text = "Journey",
                fontSize = 24.sp,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.primary,
                modifier = Modifier.padding(start = 8.dp).weight(1f),
            )
            LivesCounter(livesInfo)
            GemChip(balance = gemBalance, modifier = Modifier.padding(end = 8.dp))
            TotalStars(count = service.totalStars, max = service.maxStars)
            TextButton(onClick = { showResetConfirm = true }) {
                Text("Reset", color = MaterialTheme.colorScheme.error)
            }
        }
        HorizontalDivider()
        LazyColumn(modifier = Modifier.fillMaxSize()) {
            items(service.entries, key = { it.id }) { entry ->
                val status = when {
                    entry.id < highestUnlocked  -> LevelStatus.Completed
                    entry.id == highestUnlocked -> LevelStatus.Available
                    else                        -> LevelStatus.Locked
                }
                LevelListItem(
                    entry = entry,
                    status = status,
                    bestStars = if (status != LevelStatus.Locked) service.starsForLevel(entry.id) else 0,
                    onClick = { if (status != LevelStatus.Locked) onLevelSelected(entry.id) },
                )
                HorizontalDivider()
            }
        }
    }
}

@Composable
private fun LevelListItem(
    entry: JourneyEntry,
    status: LevelStatus,
    bestStars: Int,
    onClick: () -> Unit,
) {
    val isEnabled = status != LevelStatus.Locked
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(enabled = isEnabled, onClick = onClick)
            .padding(horizontal = 24.dp, vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column {
            Text(
                text = "Level ${entry.id}",
                fontSize = 12.sp,
                color = if (isEnabled) MaterialTheme.colorScheme.onSurface
                        else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.4f),
            )
            Text(
                text = entry.name,
                fontSize = 18.sp,
                fontWeight = FontWeight.Medium,
                color = if (isEnabled) MaterialTheme.colorScheme.onSurface
                        else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.4f),
            )
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            // Best stars are hidden for locked levels (empty for unplayed available levels).
            if (status != LevelStatus.Locked) {
                StarSlots(filled = bestStars)
                Spacer(Modifier.width(12.dp))
            }
            when (status) {
                LevelStatus.Completed -> Icon(
                    imageVector = Icons.Default.CheckCircle,
                    contentDescription = "Completed",
                    tint = MaterialTheme.colorScheme.primary,
                )
                LevelStatus.Available -> Icon(
                    imageVector = Icons.Default.PlayArrow,
                    contentDescription = "Play",
                    tint = MaterialTheme.colorScheme.secondary,
                )
                LevelStatus.Locked -> Icon(
                    imageVector = Icons.Default.Lock,
                    contentDescription = "Locked",
                    tint = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f),
                )
            }
        }
    }
}

/** Three small star slots: filled gold up to [filled], dimmed outlines for the rest. */
@Composable
private fun StarSlots(filled: Int) {
    Row(horizontalArrangement = Arrangement.spacedBy(2.dp), verticalAlignment = Alignment.CenterVertically) {
        repeat(3) { i ->
            val earned = i < filled
            Icon(
                imageVector = if (earned) Icons.Filled.Star else Icons.Filled.StarBorder,
                contentDescription = if (earned) "Star earned" else "Star not earned",
                tint = if (earned) Color(0xFFFFC107) else MaterialTheme.colorScheme.onSurface.copy(alpha = 0.3f),
                modifier = Modifier.size(18.dp),
            )
        }
    }
}

/** Header total: a gold star followed by `count / max`. */
@Composable
private fun TotalStars(count: Int, max: Int) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Icon(
            imageVector = Icons.Filled.Star,
            contentDescription = "Total stars earned",
            tint = Color(0xFFFFC107),
            modifier = Modifier.size(18.dp),
        )
        Text(
            text = " $count/$max",
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
            modifier = Modifier.padding(end = 4.dp),
        )
    }
}
